/**
 * Pure helpers of the Database "Undo last import" feature (no Firestore, unit-tested).
 *
 * A snapshot is the list of every document an apply will create, overwrite or
 * delete, as `{ collection, id, data }` where `data` is the CURRENT document
 * data, or `null` when the document does not exist yet (the apply creates it,
 * so the undo deletes it again).
 *
 * The snapshot is stored under `import_undo/<entryId>/chunks/<i>` as
 * `{ items: [...] }`, so a chunk must stay well under the 1 MB Firestore
 * document limit: at most SNAPSHOT_CHUNK_SIZE items AND at most
 * SNAPSHOT_MAX_CHUNK_BYTES of serialized JSON.
 */

/** Items per chunk (upper bound; a chunk is cut earlier when it gets too big). */
export const SNAPSHOT_CHUNK_SIZE = 300;
/** Serialized size budget of one chunk document (the Firestore limit is 1 MB). */
export const SNAPSHOT_MAX_CHUNK_BYTES = 900 * 1024;

/** Bytes of the `{ "items": [] }` wrapper around the items of a chunk. */
const CHUNK_OVERHEAD_BYTES = 12;

/** UTF-8 byte length of a string (TextEncoder is not available in every test env). */
export const utf8Length = (str) => {
    const s = String(str ?? '');
    let bytes = 0;
    for (let i = 0; i < s.length; i++) {
        const code = s.charCodeAt(i);
        if (code < 0x80) bytes += 1;
        else if (code < 0x800) bytes += 2;
        else if (code >= 0xd800 && code <= 0xdbff) { bytes += 4; i++; }   // surrogate pair
        else bytes += 3;
    }
    return bytes;
};

/** Serialized size of one snapshot item inside a chunk (plus its separating comma). */
export const snapshotItemBytes = (item) => {
    let json;
    try {
        json = JSON.stringify(item === undefined ? null : item);
    } catch (e) {
        json = '';
    }
    return utf8Length(json === undefined ? 'null' : json) + 1;
};

/** Drops entries without a collection / id and turns a missing `data` into an explicit null. */
export const normalizeSnapshotItems = (items) => (items || [])
    .filter(it => it && it.collection && it.id !== undefined && it.id !== null && String(it.id) !== '')
    .map(it => ({ collection: String(it.collection), id: String(it.id), data: it.data === undefined ? null : it.data }));

/**
 * Snapshot items -> chunks of at most `maxCount` items whose serialized
 * `{ items }` document stays under `maxBytes`. A single item bigger than the
 * budget gets its own chunk (nothing can be done about it here).
 */
export const chunkSnapshotItems = (items, { maxCount = SNAPSHOT_CHUNK_SIZE, maxBytes = SNAPSHOT_MAX_CHUNK_BYTES } = {}) => {
    const chunks = [];
    let current = [];
    let bytes = CHUNK_OVERHEAD_BYTES;

    (items || []).forEach(item => {
        const size = snapshotItemBytes(item);
        const full = current.length >= maxCount || bytes + size > maxBytes;
        if (current.length > 0 && full) {
            chunks.push(current);
            current = [];
            bytes = CHUNK_OVERHEAD_BYTES;
        }
        current.push(item);
        bytes += size;
    });

    if (current.length > 0) chunks.push(current);
    return chunks;
};

/** Documents the apply creates (no current data): the undo deletes these again. */
export const countCreates = (items) => (items || []).filter(it => !it || it.data === null || it.data === undefined).length;

/** What an undo of this entry would do: `{ docs, creates, restores }`. */
export const undoCounts = (entry) => {
    const docs = Number(entry?.docs || 0);
    const creates = Math.min(docs, Math.max(0, Number(entry?.creates || 0)));
    return { docs, creates, restores: Math.max(0, docs - creates) };
};

// ---------------------------------------------------------------------------
// "Changed since the import" check (owner, 26 Sep 2026)
//
// The engine merges fleet status / branch / KM every hour and writes the
// airport contracts; staff and contracts are edited in the app. An undo that
// blindly writes the pre-import data back would wipe all of that. So right
// after the apply, the fingerprint of every touched document AS THE IMPORT
// LEFT IT is stored next to the snapshot (`after`, one per item), and the undo
// only restores / deletes the documents whose current fingerprint still
// matches; the others are kept and listed.
// ---------------------------------------------------------------------------

/** Fingerprint of a document that does not exist. */
export const ABSENT = '-';

const isTimestampLike = (v) => v && typeof v.toMillis === 'function';
const isDocRefLike = (v) => v && typeof v.path === 'string' && v.firestore !== undefined;

/**
 * Key-order independent JSON of Firestore data: a Timestamp / Date becomes its
 * milliseconds, a DocumentReference its path (it holds the whole Firestore
 * instance, which would recurse forever).
 */
export const canonicalJson = (value, depth = 0) => {
    if (depth > 40) return '"<deep>"';
    if (value === null || value === undefined) return 'null';
    if (value instanceof Date) return `{"__ts":${value.getTime()}}`;
    if (isTimestampLike(value)) return `{"__ts":${value.toMillis()}}`;
    if (isDocRefLike(value)) return `{"__ref":${JSON.stringify(value.path)}}`;
    if (Array.isArray(value)) return `[${value.map(v => canonicalJson(v, depth + 1)).join(',')}]`;
    if (typeof value === 'object') {
        const keys = Object.keys(value).filter(k => value[k] !== undefined).sort();
        return `{${keys.map(k => `${JSON.stringify(k)}:${canonicalJson(value[k], depth + 1)}`).join(',')}}`;
    }
    if (typeof value === 'number' && !Number.isFinite(value)) return JSON.stringify(String(value));
    return JSON.stringify(value);
};

/** cyrb53: a fast 53-bit string hash (plenty to tell "changed" from "unchanged"). */
const cyrb53 = (str) => {
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < str.length; i++) {
        const ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

/** Fingerprint of one document's data (`null` / `undefined` = the document does not exist). */
export const docFingerprint = (data) => (data === null || data === undefined ? ABSENT : cyrb53(canonicalJson(data)).toString(36));

/** `collection/id` of a snapshot item, the key the current documents are looked up by. */
export const itemKey = (item) => `${item.collection}/${item.id}`;

/**
 * Fingerprints of the documents right after the apply, one per chunk item
 * (`currentByKey`: Map `collection/id` -> current data, missing = no document),
 * plus how many documents the apply really changed (before != after).
 */
export const afterStateOf = (chunks, currentByKey) => {
    let changed = 0;
    const after = (chunks || []).map(chunk => chunk.map(item => {
        const fp = docFingerprint(currentByKey.has(itemKey(item)) ? currentByKey.get(itemKey(item)) : null);
        if (fp !== docFingerprint(item.data)) changed++;
        return fp;
    }));
    return { after, changed };
};

/**
 * What an undo may touch. `items` carry `after` (the fingerprint stored right
 * after the apply); `currentByKey` is the documents as they are now. A document
 * whose fingerprint differs was changed after the import (engine merge, an edit
 * in the app, a later import) and is kept; an item without `after` cannot be
 * checked and is kept too.
 * Returns `{ restore: [items], remove: [items], skipped: [{ collection, id, reason }] }`.
 */
export const undoPlan = (items, currentByKey) => {
    const restore = [];
    const remove = [];
    const skipped = [];
    (items || []).forEach(item => {
        if (item.after === undefined || item.after === null) {
            skipped.push({ collection: item.collection, id: item.id, reason: 'not checked' });
            return;
        }
        const now = docFingerprint(currentByKey.has(itemKey(item)) ? currentByKey.get(itemKey(item)) : null);
        if (now !== item.after) {
            skipped.push({ collection: item.collection, id: item.id, reason: 'changed after the import' });
            return;
        }
        if (item.data === null || item.data === undefined) {
            // created by the import and untouched since: delete it (nothing to do when already gone)
            if (now !== ABSENT) remove.push(item);
        } else {
            restore.push(item);
        }
    });
    return { restore, remove, skipped };
};

/**
 * May this snapshot be undone? `entry` is `import_undo/<entryId>`, `log` is
 * `import_log/<table>`. Only the LATEST import of a table can be undone: the
 * table log keeps the id of the entry that wrote it (`entry_id`), so a newer
 * import (or a previous undo) makes the snapshot unusable.
 * Returns `{ allowed, reason }`.
 */
export const undoGuard = ({ entryId, entry, log } = {}) => {
    if (!entry) return { allowed: false, reason: 'No snapshot was stored for this import.' };
    if (entry.status === 'undone') return { allowed: false, reason: 'This import was already undone.' };
    if (entry.status === 'no_snapshot') return { allowed: false, reason: 'The snapshot of this import could not be written, so it cannot be undone.' };
    if (entry.status === 'expired') return { allowed: false, reason: 'The snapshot of this import was replaced by a newer one.' };
    if (entry.status !== 'ready') return { allowed: false, reason: `The snapshot of this import is not usable (status "${entry.status || 'unknown'}").` };
    if (!entry.table) return { allowed: false, reason: 'The snapshot does not say which table it belongs to.' };
    // Without the after-import fingerprints the undo cannot tell a document changed since the
    // import from one that was not, and would overwrite the later changes (audit ADM-04).
    if (entry.after_saved !== true) {
        return { allowed: false, reason: 'The state after this import was not recorded, so the undo cannot tell which documents were changed since; it cannot be undone safely.' };
    }

    const id = entryId || entry.id || null;
    if (!log || !log.entry_id) return { allowed: false, reason: `No import is recorded for "${entry.table}" any more, so this snapshot can no longer be matched to it.` };
    if (!id || log.entry_id !== id) {
        return { allowed: false, reason: `"${entry.table}" was imported again after this one; only the latest import can be undone.` };
    }
    return { allowed: true, reason: null };
};

const importUndo = { chunkSnapshotItems, normalizeSnapshotItems, undoGuard, undoCounts, countCreates, docFingerprint, afterStateOf, undoPlan };
export default importUndo;
